/**
 * ImportUsersWizard - Multi-step wizard for bulk user import.
 *
 * Steps:
 *  1. Upload - Select a file (xlsx, csv, json, etc.)
 *  2. Parsing - AI parses the file (PubSub progress)
 *  3. Preview - Review & edit parsed rows
 *  4. Confirm - Execute invitations with progress
 *  5. Results - Summary of created/failed users
 *
 * Chrome (dialog, step rail, error banner, footer) comes from
 * `ImportWizardShell`, shared with the clients wizard.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiUsersLight,
  PiUploadSimpleLight,
  PiSparkleLight,
  PiTableLight,
  PiPaperPlaneTiltLight,
  PiCheckCircleLight,
} from 'react-icons/pi';

import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Badge from '../shared/TailwindComponents/Badge';
import { FIELD_HELP_CLASS } from '../shared/TailwindComponents/FieldShell';
import type { WizardStepRailItem } from '../shared/FormComponents/WizardStepRail';
import ImportWizardShell, {
  ImportProcessingState,
  ImportResultHeader,
  ImportStatStrip,
  ImportFailureList,
  ImportSelectionToggles,
  ImportRowActions,
  IMPORT_TH_CLASS,
  importRowClass,
  type ImportStat,
} from './ImportWizardShell';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import { useImportPubSub } from '../../hooks/useImportPubSub';
import {
  uploadUsersImport,
  getImportStatus,
  updateImportPreview,
  confirmImport,
} from '../../services/import/importApi';
import type {
  ImportWizardStep,
  ImportUserRow,
  ImportPreviewData,
  ImportConfirmResponse,
} from '../../types/import';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

interface ImportUsersWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS: WizardStepRailItem<ImportWizardStep>[] = [
  { key: 'upload', label: 'Upload File', icon: <PiUploadSimpleLight className="w-4 h-4" /> },
  { key: 'parsing', label: 'AI Parsing', icon: <PiSparkleLight className="w-4 h-4" /> },
  { key: 'preview', label: 'Preview & Edit', icon: <PiTableLight className="w-4 h-4" /> },
  { key: 'confirm', label: 'Importing', icon: <PiPaperPlaneTiltLight className="w-4 h-4" /> },
  { key: 'results', label: 'Results', icon: <PiCheckCircleLight className="w-4 h-4" /> },
];

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

/** A row the org's domain policy excludes from import. */
const isDomainMismatch = (row: ImportUserRow) =>
  row.domain_match === false || (row.warnings || []).includes('email_domain_mismatch');

export default function ImportUsersWizard({ isOpen, onClose, onComplete }: ImportUsersWizardProps) {
  const { t } = useTranslation();

  // Wizard state
  const [step, setStep] = useState<ImportWizardStep>('upload');
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(null);
  const [confirmResult, setConfirmResult] = useState<ImportConfirmResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);

  // PubSub hook for AI parsing progress
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: step === 'parsing',
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadPreview(uuid || jobUuid!);
        setStep('preview');
      }
    },
    onFailed: (err) => {
      setError(err);
      setStep('upload');
    },
  });

  // Fetch available roles on open
  useEffect(() => {
    if (!isOpen) return;
    const fetchRoles = async () => {
      try {
        const response = await apis.get('/admin/roles');
        if (response.data.success && response.data.data) {
          const opts: DropdownOption[] = response.data.data.map((role: any) => ({
            value: role.name,
            label: formatRoleNameForDisplay(role.name),
          }));
          setRoleOptions(opts);
        }
      } catch {
        // Fallback defaults if roles API fails
        setRoleOptions([
          { value: 'full_access', label: t('Full access') },
          { value: 'edit_access', label: t('Edit access') },
          { value: 'read_access', label: t('Read access') },
        ]);
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setJobUuid(null);
      setCorrelationId(null);
      setSelectedFile(null);
      setPreviewData(null);
      setConfirmResult(null);
      setIsLoading(false);
      setError(null);
      setEditingRow(null);
      resetProgress();
    }
  }, [isOpen, resetProgress]);

  // ── Step 1: File Upload ──
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await uploadUsersImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setStep('parsing');
      } else {
        const e = response.error;
        setError(toErrorText(e) || t('Upload failed'));
      }
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Upload failed'));
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, t]);

  // ── Step 2: Load preview from API ──
  const loadPreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        setPreviewData(response.preview);
        // Select all rows by default and set default role
        const rows = response.preview.rows as ImportUserRow[];
        rows.forEach((row) => {
          row._selected = !row.is_duplicate && !isDomainMismatch(row);
          if (!row.role) {
            row.role = 'full_access';
          }
        });
        setPreviewData({ ...response.preview, rows });
      }
    } catch {
      setError(t('Failed to load preview'));
    }
  }, [t]);

  // ── Step 3: Preview editing ──
  const toggleRow = useCallback((idx: number) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows[idx] = { ...rows[idx], _selected: !rows[idx]._selected };
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const toggleAll = useCallback((selected: boolean) => {
    if (!previewData) return;
    const rows = (previewData.rows as ImportUserRow[]).map((r) => ({
      ...r,
      _selected: (r.is_duplicate || isDomainMismatch(r)) ? false : selected,
    }));
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const updateRow = useCallback((idx: number, updates: Partial<ImportUserRow>) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows[idx] = { ...rows[idx], ...updates, _edited: true };
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const removeRow = useCallback((idx: number) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows.splice(idx, 1);
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  // ── Step 4: Confirm ──
  const handleConfirm = useCallback(async () => {
    if (!jobUuid || !previewData) return;

    const selectedRows = (previewData.rows as ImportUserRow[]).filter((r) => r._selected);
    if (selectedRows.length === 0) {
      setError(t('No rows selected for import'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save the current preview (with selections/edits) first
      await updateImportPreview(jobUuid, selectedRows, {
        total_rows: selectedRows.length,
      });

      // Execute the import
      setStep('confirm');
      const result = await confirmImport(jobUuid);
      setConfirmResult(result);
      setStep('results');
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Import failed'));
      setError(errorMsg);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, previewData, t]);

  const handleClose = useCallback(() => {
    if (confirmResult && confirmResult.created_count > 0) {
      onComplete?.();
    }
    onClose();
  }, [confirmResult, onComplete, onClose]);

  // ── Derived ──
  const rows = previewData?.rows as ImportUserRow[] | undefined;
  const selectedCount = rows?.filter((r) => r._selected).length || 0;
  const duplicateCount = rows?.filter((r) => r.is_duplicate).length || 0;
  const domainMismatchCount = rows?.filter(isDomainMismatch).length || 0;

  const resultStats: ImportStat[] = confirmResult
    ? [
        { value: confirmResult.created_count, label: t('Users invited'), tone: 'success' },
        ...(confirmResult.skipped_existing
          ? [{ value: confirmResult.skipped_existing, label: t('Already existed'), tone: 'warning' as const }]
          : []),
        { value: confirmResult.failed_count, label: t('Failed'), tone: 'danger' },
      ]
    : [];

  // ── Footer ──
  const footerLeading =
    step === 'preview' ? (
      <div className="flex flex-col gap-0.5">
        <span className="text-sm text-fg-muted">
          {t('{{count}} users will be invited', { count: selectedCount })}
        </span>
        {domainMismatchCount > 0 && (
          <span className="text-xs text-danger-500">
            {t('{{count}} users excluded — email domain does not match your organization', { count: domainMismatchCount })}
          </span>
        )}
      </div>
    ) : null;

  const footerActions = (
    <>
      {(step === 'upload' || step === 'results') && (
        <Button variant="secondary" size="small" onClick={handleClose}>
          {step === 'results' ? t('Done') : t('Cancel')}
        </Button>
      )}
      {step === 'upload' && (
        <Button
          variant="primary"
          size="small"
          onClick={handleUpload}
          disabled={!selectedFile || isLoading}
          isLoading={isLoading}
        >
          {t('Upload & parse')}
        </Button>
      )}
      {step === 'preview' && (
        <>
          <Button variant="secondary" size="small" onClick={() => { setStep('upload'); setPreviewData(null); }}>
            {t('Back')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isLoading}
            isLoading={isLoading}
          >
            {t('Confirm & import {{count}} users', { count: selectedCount })}
          </Button>
        </>
      )}
    </>
  );

  return (
    <ImportWizardShell
      isOpen={isOpen}
      icon={<PiUsersLight />}
      title={t('Import users')}
      steps={STEPS}
      currentStep={step}
      error={error}
      onDismissError={() => setError(null)}
      onClose={handleClose}
      footerLeading={footerLeading}
      footerActions={footerActions}
    >
      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-6">
          <div className="text-center">
            <h3 className="text-sm font-medium text-fg-body">
              {t('Upload a file with user data')}
            </h3>
            <p className="mt-1 text-sm text-fg-muted">
              {t('Supported formats: Excel (.xlsx), CSV, JSON, PDF, DOCX, TXT')}
            </p>
            <p className={`mt-1 ${FIELD_HELP_CLASS}`}>
              {t('AI will automatically parse and normalize the data regardless of format.')}
            </p>
          </div>

          <div className="w-full">
            <FileUploader
              files={selectedFile ? [selectedFile] : []}
              onFilesChange={(files) => {
                setSelectedFile(files[0] || null);
                setError(null);
              }}
              allowedExtensions={ALLOWED_EXTENSIONS}
              maxFiles={1}
            />
          </div>
        </div>
      )}

      {/* Step: Parsing - the one AI moment, so the one logo loader. */}
      {step === 'parsing' && (
        <ImportProcessingState
          ai
          title={t('Analysing your file...')}
          detail={progress.message || t('This usually takes 10-30 seconds')}
        />
      )}

      {/* Step: Preview */}
      {step === 'preview' && rows && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-fg-body">
                {t('Total')}: <strong className="tabular-nums">{rows.length}</strong>
              </span>
              <span className="text-success-600 dark:text-success-400">
                {t('Selected')}: <strong className="tabular-nums">{selectedCount}</strong>
              </span>
              {duplicateCount > 0 && (
                <span className="text-warning-600 dark:text-warning-400">
                  {t('Duplicates')}: <strong className="tabular-nums">{duplicateCount}</strong>
                </span>
              )}
              {domainMismatchCount > 0 && (
                <span className="text-danger-500">
                  {t('Domain mismatch')}: <strong className="tabular-nums">{domainMismatchCount}</strong>
                </span>
              )}
            </div>
            <ImportSelectionToggles
              onSelectAll={() => toggleAll(true)}
              onSelectNone={() => toggleAll(false)}
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken/70">
                <tr>
                  <th className={`${IMPORT_TH_CLASS} w-10`}>
                    <CheckBox
                      hideLabel
                      label={t('Select all rows')}
                      translate={false}
                      checked={selectedCount > 0 && selectedCount === rows.filter((r) => !r.is_duplicate && !isDomainMismatch(r)).length}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th className={IMPORT_TH_CLASS}>{t('First name')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Last name')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Email')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Role')}</th>
                  <th className={`${IMPORT_TH_CLASS} w-28`}>{t('Status')}</th>
                  <th className={`${IMPORT_TH_CLASS} w-16`} />
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline-soft">
                {rows.map((row, idx) => {
                  const blocked = isDomainMismatch(row);
                  return (
                    <tr
                      key={idx}
                      className={importRowClass({
                        duplicate: !!row.is_duplicate,
                        blocked,
                        selected: !!row._selected,
                      })}
                    >
                      <td className="px-3 py-2">
                        <CheckBox
                          hideLabel
                          label={t('Select {{email}}', { email: row.email })}
                          translate={false}
                          checked={!!row._selected}
                          disabled={!!row.is_duplicate || blocked}
                          onChange={() => toggleRow(idx)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {editingRow === idx ? (
                          <Input
                            type="text"
                            value={row.first_name}
                            onChange={(e) => updateRow(idx, { first_name: e.target.value })}
                          />
                        ) : (
                          <span className="text-fg-body">{row.first_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingRow === idx ? (
                          <Input
                            type="text"
                            value={row.last_name}
                            onChange={(e) => updateRow(idx, { last_name: e.target.value })}
                          />
                        ) : (
                          <span className="text-fg-body">{row.last_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingRow === idx ? (
                          <Input
                            type="email"
                            value={row.email}
                            onChange={(e) => updateRow(idx, { email: e.target.value })}
                          />
                        ) : (
                          <span className="text-fg-body">{row.email}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingRow === idx ? (
                          <Dropdown
                            options={roleOptions}
                            placeholder={t('Select role')}
                            value={row.role}
                            onChange={(val) => {
                              const v = Array.isArray(val) ? val[0] : val;
                              updateRow(idx, { role: v });
                            }}
                            compact
                          />
                        ) : (
                          <Badge color="blue">
                            {row.role ? formatRoleNameForDisplay(row.role) : '—'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.is_duplicate ? (
                          <Badge color="yellow">{t('Duplicate')}</Badge>
                        ) : blocked ? (
                          <Badge
                            color="red"
                            tooltip={row.warning || t('Email domain does not match your organization')}
                          >
                            {t('Domain mismatch')}
                          </Badge>
                        ) : row.warning ? (
                          <Badge color="yellow" tooltip={row.warning}>{t('Warning')}</Badge>
                        ) : (
                          <Badge color="green">{t('Ready')}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <ImportRowActions
                          editing={editingRow === idx}
                          onEdit={() => setEditingRow(editingRow === idx ? null : idx)}
                          onRemove={() => removeRow(idx)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step: Confirm - ordinary work, so the plain spinner. */}
      {step === 'confirm' && (
        <ImportProcessingState
          title={t('Creating user accounts & sending invitations...')}
          detail={t('This may take a moment. Please do not close this window.')}
        />
      )}

      {/* Step: Results */}
      {step === 'results' && confirmResult && (
        <div className="space-y-6">
          <ImportResultHeader
            succeeded={confirmResult.created_count > 0}
            title={confirmResult.created_count > 0 ? t('Import completed') : t('Import failed')}
            detail={t('{{created}} of {{total}} users imported successfully', {
              created: confirmResult.created_count,
              total: confirmResult.created_count + confirmResult.failed_count,
            })}
          />

          <ImportStatStrip stats={resultStats} />

          {confirmResult.failed_items.length > 0 && (
            <ImportFailureList
              title={t('Failed items')}
              items={confirmResult.failed_items.map((item) =>
                t('Row {{row}}{{who}}: {{error}}', {
                  row: item.row + 1,
                  who: item.email ? ` (${item.email})` : '',
                  error: item.error,
                })
              )}
            />
          )}
        </div>
      )}
    </ImportWizardShell>
  );
}
